import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { Button, Spinner, Alert } from '../ui';
import { Check, X, Shield, Filter, RefreshCw } from 'lucide-react';

interface PermissionItem {
  id: string;
  item_name: string;
  item_type: 'tool' | 'prompt' | 'resource';
  server_id: string;
  server_name: string;
  permission_type: 'allow' | 'deny' | null;
  effective_permission: 'allow' | 'deny' | 'allow_all' | 'deny_all';
}

interface PermissionsManagerProps {
  clientId: string;
  clientName: string;
  onClose: () => void;
}

export const PermissionsManager: React.FC<PermissionsManagerProps> = ({
  clientId,
  clientName,
  onClose
}) => {
  const [items, setItems] = useState<PermissionItem[]>([]);
  const [defaultPermission, setDefaultPermission] = useState<'allow_all' | 'deny_all'>('allow_all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'tool' | 'prompt' | 'resource'>('all');
  const [filterServer, setFilterServer] = useState<string>('all');
  const [filterPermission, setFilterPermission] = useState<'all' | 'allowed' | 'denied'>('all');
  const [hasChanges, setHasChanges] = useState(false);

  // Cargar permisos al montar el componente
  useEffect(() => {
    fetchPermissions();
  }, [clientId]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.clients.permissions.list(clientId);
      setItems(response.items);
      setDefaultPermission(response.default_permission);
    } catch (err: any) {
      setError(err.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (itemId: string, newPermission: 'allow' | 'deny' | null) => {
    setItems(items.map(item =>
      item.id === itemId
        ? {
            ...item,
            permission_type: newPermission,
            effective_permission: newPermission || defaultPermission
          }
        : item
    ));
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      setError('');

      // Preparar permisos para actualización masiva
      const permissions = items
        .filter(item => item.permission_type !== null)
        .map(item => ({
          item_id: item.id,
          permission_type: item.permission_type
        }));

      await api.clients.permissions.bulkUpdate(clientId, permissions);
      setHasChanges(false);
      await fetchPermissions();
    } catch (err: any) {
      setError(err.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar todos los permisos explícitos? Esto volverá a usar el permiso por defecto para todos los items.')) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      await api.clients.permissions.deleteAll(clientId);
      setHasChanges(false);
      await fetchPermissions();
    } catch (err: any) {
      setError(err.message || 'Failed to reset permissions');
    } finally {
      setSaving(false);
    }
  };

  // Obtener lista de servidores únicos
  const servers = Array.from(new Set(items.map(item => item.server_name))).sort();

  // Filtrar items
  const filteredItems = items.filter(item => {
    if (filterType !== 'all' && item.item_type !== filterType) return false;
    if (filterServer !== 'all' && item.server_name !== filterServer) return false;
    if (filterPermission === 'allowed' && item.effective_permission !== 'allow' && item.effective_permission !== 'allow_all') return false;
    if (filterPermission === 'denied' && item.effective_permission !== 'deny' && item.effective_permission !== 'deny_all') return false;
    return true;
  });

  // Agrupar por tipo
  const groupedItems = {
    tool: filteredItems.filter(item => item.item_type === 'tool'),
    prompt: filteredItems.filter(item => item.item_type === 'prompt'),
    resource: filteredItems.filter(item => item.item_type === 'resource')
  };

  const getPermissionIcon = (effective: string) => {
    if (effective === 'allow' || effective === 'allow_all') {
      return <Check className="text-green-600" size={18} />;
    }
    return <X className="text-red-600" size={18} />;
  };

  const getPermissionBadge = (item: PermissionItem) => {
    const isExplicit = item.permission_type !== null;
    const isAllowed = item.effective_permission === 'allow' || item.effective_permission === 'allow_all';

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${
        isAllowed
          ? 'bg-green-100 text-green-800'
          : 'bg-red-100 text-red-800'
      }`}>
        {isExplicit ? (isAllowed ? 'Allow (explicit)' : 'Deny (explicit)') : (isAllowed ? 'Allow (default)' : 'Deny (default)')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Manage Permissions</h2>
          <p className="text-gray-600 mt-1">
            Control access to tools, prompts, and resources for <span className="font-semibold">{clientName}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-sm">
            <span className="text-gray-600">Default: </span>
            <span className={`font-semibold ${defaultPermission === 'allow_all' ? 'text-green-600' : 'text-red-600'}`}>
              {defaultPermission === 'allow_all' ? 'Allow All' : 'Deny All'}
            </span>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Types</option>
            <option value="tool">Tools</option>
            <option value="prompt">Prompts</option>
            <option value="resource">Resources</option>
          </select>

          <select
            value={filterServer}
            onChange={(e) => setFilterServer(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Servers</option>
            {servers.map(server => (
              <option key={server} value={server}>{server}</option>
            ))}
          </select>

          <select
            value={filterPermission}
            onChange={(e) => setFilterPermission(e.target.value as any)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Permissions</option>
            <option value="allowed">Allowed Only</option>
            <option value="denied">Denied Only</option>
          </select>

          <button
            onClick={fetchPermissions}
            className="ml-auto flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-sm text-blue-600 font-medium">Tools</div>
          <div className="text-2xl font-bold text-blue-900">{groupedItems.tool.length}</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="text-sm text-purple-600 font-medium">Prompts</div>
          <div className="text-2xl font-bold text-purple-900">{groupedItems.prompt.length}</div>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <div className="text-sm text-orange-600 font-medium">Resources</div>
          <div className="text-2xl font-bold text-orange-900">{groupedItems.resource.length}</div>
        </div>
      </div>

      {/* Permissions List */}
      <div className="space-y-6 max-h-[500px] overflow-y-auto">
        {Object.entries(groupedItems).map(([type, typeItems]) => {
          if (typeItems.length === 0) return null;

          return (
            <div key={type} className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900 capitalize flex items-center gap-2">
                {type === 'tool' && <Shield size={20} className="text-blue-600" />}
                {type === 'prompt' && <Shield size={20} className="text-purple-600" />}
                {type === 'resource' && <Shield size={20} className="text-orange-600" />}
                {type}s ({typeItems.length})
              </h3>
              <div className="space-y-1">
                {typeItems.map(item => (
                  <div
                    key={item.id}
                    className="bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {getPermissionIcon(item.effective_permission)}
                          <span className="font-medium text-gray-900">{item.item_name}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Server: {item.server_name}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {getPermissionBadge(item)}

                        <div className="flex gap-1">
                          <button
                            onClick={() => handlePermissionChange(item.id, item.permission_type === 'allow' ? null : 'allow')}
                            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                              item.permission_type === 'allow'
                                ? 'bg-green-600 text-white border-green-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-green-50 hover:border-green-600'
                            }`}
                          >
                            Allow
                          </button>
                          <button
                            onClick={() => handlePermissionChange(item.id, item.permission_type === 'deny' ? null : 'deny')}
                            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                              item.permission_type === 'deny'
                                ? 'bg-red-600 text-white border-red-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-red-50 hover:border-red-600'
                            }`}
                          >
                            Deny
                          </button>
                          {item.permission_type !== null && (
                            <button
                              onClick={() => handlePermissionChange(item.id, null)}
                              className="px-3 py-1.5 text-xs rounded-lg border bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                              title="Reset to default"
                            >
                              Default
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No items found matching the selected filters.
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <Button
          variant="ghost"
          onClick={handleResetAll}
          disabled={saving || items.every(item => item.permission_type === null)}
        >
          Reset All to Default
        </Button>

        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveChanges}
            loading={saving}
            disabled={!hasChanges}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};
